import React, { KeyboardEvent } from 'react';
import PropTypes from 'prop-types';
import { st, classes } from './DatePickerDropdown.st.css';
import { ChevronDown } from '@wix/wix-ui-icons-common';
import DropdownBase from '../../DropdownBase';
import TextButton from '../../TextButton';
import { DropdownLayoutValueOption } from '../../DropdownLayout';
import { DatePickerDropdownProps } from './DatePickerDropdown.types';

const YEAR_ADD = 15;

type DropdownPickerState = {
  open: boolean;
  visibleOptions: DatePickerDropdownProps['options'];
};

export default class DropdownPicker extends React.Component<
  DatePickerDropdownProps,
  DropdownPickerState
> {
  static propTypes = {
    dataHook: PropTypes.string,
    className: PropTypes.string,
    caption: PropTypes.node,
    options: PropTypes.array,
    onChange: PropTypes.func,
    selectedId: PropTypes.number,
    ariaLabel: PropTypes.string,
    ariaLabelledBy: PropTypes.string,
  };

  state = {
    open: false,
    visibleOptions: this.props.options.slice(
      0,
      this.props.selectedId + YEAR_ADD,
    ),
  };

  componentDidUpdate(prevProps: DatePickerDropdownProps) {
    if (
      prevProps.options.length !== this.props.options.length ||
      this.props.selectedId >= this.state.visibleOptions.length
    ) {
      this.setState({
        visibleOptions: this.props.options.slice(
          0,
          this.props.selectedId + YEAR_ADD,
        ),
      });
    }
  }

  _onSelect = (data: DropdownLayoutValueOption) => {
    const { onChange } = this.props;

    this.setState(
      {
        open: false,
      },
      () => {
        if (typeof onChange === 'function') {
          onChange(data);
        }
      },
    );
  };

  _toggle = () => {
    this.setState({ open: !this.state.open });
  };

  _onKeyDown = (
    e: KeyboardEvent,
    delegateKeyDown: (event: React.KeyboardEvent<Element>) => boolean | void,
  ) => {
    const eventWasHandled = delegateKeyDown(e);
    const { open } = this.state;
    // We'll open the list when pressing the ArrowDown key
    if (!eventWasHandled && e.key === 'ArrowDown') {
      this._open();
      e.preventDefault();
      return;
    }

    // close on Escape
    if (e.key === 'Escape') {
      this._close();
      e.preventDefault();
    }

    // prevent TextButton onClick event
    if (open && (e.key === 'Enter' || e.key === 'Spacebar' || e.key === ' ')) {
      e.preventDefault();
    }
  };

  _close = () => {
    this.setState({ open: false });
  };

  _open = () => {
    this.setState({ open: true });
  };

  _fetchMoreOptions = () => {
    const length = this.state.visibleOptions.length;
    this.setState(({ visibleOptions }) => ({
      visibleOptions: [
        ...visibleOptions,
        ...this.props.options.slice(length, length + YEAR_ADD),
      ],
    }));
  };

  render() {
    const {
      className,
      caption,
      options,
      dataHook,
      selectedId,
      ariaLabel,
      ariaLabelledBy,
    } = this.props;

    const { open } = this.state;

    const finalAriaLabel = ariaLabel ? `${ariaLabel} ${caption}` : undefined;

    return (
      <div className={st(classes.root, className)}>
        <DropdownBase
          dataHook={dataHook}
          className={classes.dropdown}
          options={this.state.visibleOptions}
          onClickOutside={this._close}
          dynamicWidth
          minWidth={120}
          selectedId={selectedId}
          onSelect={this._onSelect}
          focusOnSelectedOption
          open={open}
          infiniteScroll
          hasMore={this.state.visibleOptions.length < options.length}
          loadMore={this._fetchMoreOptions}
          autoFocus
        >
          {({ delegateKeyDown, listboxId, activeDescendantId }) => {
            return (
              <TextButton
                className={classes.caption}
                skin="dark"
                size="small"
                suffixIcon={<ChevronDown />}
                onClick={this._toggle}
                dataHook={`${dataHook}-button`}
                onKeyDown={(e: KeyboardEvent) =>
                  this._onKeyDown(e, delegateKeyDown)
                }
                ariaLabel={finalAriaLabel}
                ariaLabelledBy={ariaLabelledBy}
                ariaHaspopup="listbox"
                ariaExpanded={open}
                ariaControls={listboxId}
                ariaActiveDescendant={activeDescendantId}
              >
                {caption}
              </TextButton>
            );
          }}
        </DropdownBase>
      </div>
    );
  }
}
